import { useHistory } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

/**
 * Custom hook for navigation that replaces window.location.href usage
 * Provides a consistent way to navigate throughout the application
 */
export const useNavigation = () => {
  const history = useHistory();

  const navigate = {
    // Navigate to a specific path
    to: (path, options = {}) => {
      if (options.replace) {
        history.replace(path);
      } else {
        history.push(path);
      }
    },

    // Navigate back
    back: () => {
      history.goBack();
    },

    // Navigate forward
    forward: () => {
      history.goForward();
    },

    // Reload the current page
    reload: () => {
      window.location.reload();
    },

    // Navigate to home/discover based on user role
    toHome: () => {
      const role = localStorage.getItem('role');
      const ROLE = {
        CREATOR_SUB: 'creator_sub',
        CREATOR_CONTENT: 'creator_content'
      };

      if (role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT) {
        history.push(ROUTES.MY_PROFILE);
      } else {
        history.push(ROUTES.DISCOVER);
      }
    },

    toFeed: () => {
      history.push(ROUTES.MODERN_FEED);
    },

    toSubscriptions: () => {
      history.push(ROUTES.MODERN_SUBSCRIPTIONS);
    },

    toPurchased: () => {
      history.push(ROUTES.MODERN_PURCHASED);
    },

    toContactSupport: () => {
      history.push(ROUTES.CONTACT_US);
    },

    toTransactions: () => {
      history.push(ROUTES.TRANSACTIONS);
    },

    toEarningsPayout: () => {
      history.push(ROUTES.EARNINGS_PAYOUT);
    },

    toEarningsReport: (year) => {
      history.push(`${ROUTES.EARNINGS_REPORT}/${year}`);
    },

    toVatNumber: () => {
      history.push(ROUTES.VAT_NUMBER);
    },

    toGstNumber: () => {
      history.push(ROUTES.GST_NUMBER);
    },

    toBankAccount: () => {
      history.push(ROUTES.BANK_ACCOUNT);
    },

    toW9TaxInfo: () => {
      history.push(ROUTES.W9_TAX_INFO);
    },

    toW8TaxInfo: () => {
      history.push(ROUTES.W8_TAX_INFO);
    },

    // Navigate to in-app webview (iframe wrapper)
    toWebView: (url, title = '') => {
      const params = new URLSearchParams();
      if (url) params.set('url', url);
      if (title) params.set('title', title);
      history.push(`${ROUTES.WEBVIEW}?${params.toString()}`);
    },

    toProfileTags: () => {
      history.push(ROUTES.PROFILE_TAGS);
    },

    // Navigate to help
    toHelp: () => {
      history.push(ROUTES.HELP);
    },

    // Navigate to login
    toLogin: () => {
      // history.push('/login');
      window.location.assign(ROUTES.LOGIN);
    },

    // Navigate to signup
    toSignup: () => {
      history.push(ROUTES.SIGNUP);
    },

    // Navigate to creator signup flow
    toCreatorSignup: () => {
      history.push(ROUTES.CREATOR_SIGNUP);
    },

    toUnderReview: () => {
      history.push(ROUTES.UNDER_REVIEW);
    },

    // Navigate to subscriber signup flow
    toSubscriberSignup: () => {
      history.push(ROUTES.SUBSCRIBER_SIGNUP);
    },


    // Pending route for customize code
    toResendVerification: (email = null) => {
      // Use URLSearchParams so special chars like "+" are preserved correctly.
      // Example: "abcone+mx@yopmail.com" will be encoded and decoded back to the same value.
      const url = email
        ? `${ROUTES.RESEND_VERIFICATION}?${new URLSearchParams({ email }).toString()}`
        : ROUTES.RESEND_VERIFICATION;
      history.push(url);
    },


    // Navigate to profile
    toProfile: (username) => {
      if (username) {
        // history.push(`/${username}`);
        // Hard redirect to avoid SPA state mixing between different user profiles.
        window.location.assign(`/${encodeURIComponent(username)}`);
      } else {
        history.push(ROUTES.MY_PROFILE);
      }
    },

    // Navigate to discover
    toDiscover: () => {
      history.push(ROUTES.DISCOVER);
    },

    // Navigate to forgot password
    toForgotPassword: () => {
      history.push(ROUTES.FORGOT_PASSWORD);
    },

    // Navigate to messages (optionally with tab: 'all' | 'unread-priority' | 'priority' | 'messages' | 'archived')
    toMessages: (tab = null) => {
      const path = tab ? `${ROUTES.MY_MESSAGES}?tab=${encodeURIComponent(tab)}` : ROUTES.MY_MESSAGES;
      history.push(path);
    },

    // Navigate to the new chat intro screen for a specific user
    toNewChat: (userId = null, options = {}) => {
      if (!userId) {
        history.push(ROUTES.NEW_MESSAGE);
        return;
      }

      const path = `${ROUTES.NEW_CHAT_INTRO}/${userId}`;
      if (options.state && Object.keys(options.state).length > 0) {
        history.push(path, options.state);
      } else {
        history.push(path);
      }
    },

    toChat: (userId = null, options = {}) => {
      if (userId) {
        const path = `/my/messages/chat/${userId}`;
        if (options.replace) {
          history.replace(path, options.state && Object.keys(options.state).length > 0 ? options.state : undefined);
          return;
        }
        if (options.state && Object.keys(options.state).length > 0) {
          history.push(path, options.state);
        } else {
          history.push(path);
        }
      } else {
        history.push(ROUTES.MY_MESSAGES);
      }
    },

    toNewMessage: () => {
      history.push(ROUTES.NEW_MESSAGE);
    },

    toCall: (opts = {}) => {
      const q = new URLSearchParams();
      if (opts?.roomId) q.set('roomId', opts.roomId);
      q.set('type', 'voice');
      q.set('callType', opts?.callType || 'outgoing');
      if (opts?.pricingModel) q.set('pricingModel', String(opts.pricingModel));
      if (opts?.perMinuteAmount != null) q.set('perMinuteAmount', String(opts.perMinuteAmount));
      if (opts?.minDurationMinutes != null) q.set('minDurationMinutes', String(opts.minDurationMinutes));
      if (opts?.flatRateAmount != null) q.set('flatRateAmount', String(opts.flatRateAmount));
      if (opts?.flatRateDurationMinutes != null) q.set('flatRateDurationMinutes', String(opts.flatRateDurationMinutes));
      const query = q.toString();
      const path = query ? `/call?${query}` : '/call';
      if (opts?.replace) {
        history.replace(path, opts?.state);
      } else {
        history.push(path, opts?.state);
      }
    },

    toVideo: (opts = {}) => {
      const q = new URLSearchParams();
      if (opts?.roomId) q.set('roomId', opts.roomId);
      q.set('type', 'video');
      q.set('callType', opts?.callType || 'outgoing');
      if (opts?.pricingModel) q.set('pricingModel', String(opts.pricingModel));
      if (opts?.perMinuteAmount != null) q.set('perMinuteAmount', String(opts.perMinuteAmount));
      if (opts?.minDurationMinutes != null) q.set('minDurationMinutes', String(opts.minDurationMinutes));
      if (opts?.flatRateAmount != null) q.set('flatRateAmount', String(opts.flatRateAmount));
      if (opts?.flatRateDurationMinutes != null) q.set('flatRateDurationMinutes', String(opts.flatRateDurationMinutes));
      const query = q.toString();
      const path = query ? `/call?${query}` : '/call';
      if (opts?.replace) {
        history.replace(path, opts?.state);
      } else {
        history.push(path, opts?.state);
      }
    },

    toCallSummary: (options = {}) => {
      const q = new URLSearchParams();
      const query = options?.query || {};
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          q.set(key, String(value));
        }
      });
      const queryString = q.toString();
      const path = queryString ? `/call/summary?${queryString}` : '/call/summary';

      if (options?.replace) {
        history.replace(path, options?.state);
      } else {
        history.push(path, options?.state);
      }
    },

    // Navigate to activity
    toActivity: () => {
      history.push(ROUTES.ACTIVITY);
    },


    // Navigate to all creators
    toAllCreators: (category = null) => {
      if (category) {
        history.push(`${ROUTES.ALL_CREATORS}?category=${encodeURIComponent(category.toLowerCase())}`);
      } else {
        history.push(ROUTES.ALL_CREATORS);
      }
    },

    toTrendingCreators: () => {
      history.push(ROUTES.ALL_CREATORS + '?sortBy=trendingCreator');
    },

    toFeaturedCreators: () => {
      history.push(ROUTES.ALL_CREATORS + '?sortBy=featuredCreator');
    },

    // Navigate to all categories
    toAllCategories: () => {
      history.push(ROUTES.ALL_CATEGORIES);
    },

    // Navigate to external URL (for payment URLs, etc.)
    toExternal: (url) => {
      window.open(url, '_blank');
    },

    // Navigate to payment URL
    toPayment: (paymentUrl) => {
      window.location.href = paymentUrl; // Keep this as window.location.href for external payment URLs
    },

    // Navigate to terms
    toTerms: () => {
      history.push(ROUTES.LEGAL_TERMS);
    },

    toPrivacy: () => {
      history.push(ROUTES.LEGAL_PRIVACY);
    },

    toCCPA: () => {
      history.push(ROUTES.LEGAL_CCPA);
    },

    toCreatorAgreement: () => {
      history.push(ROUTES.LEGAL_CREATOR_AGREEMENT);
    },

    toCopyright: () => {
      history.push(ROUTES.LEGAL_COPYRIGHT);
    },

    toCompliance: () => {
      history.push(ROUTES.LEGAL_COMPLIANCE);
    },

    toReferral: () => {
      history.push(ROUTES.LEGAL_REFERRAL);
    },

    toInfringer: () => {
      history.push(ROUTES.LEGAL_INFRINGER);
    },

    // Get current pathname
    getCurrentPath: () => {
      return history.location.pathname;
    },

    // Check if current path includes a specific string
    pathIncludes: (pathString) => {
      return history.location.pathname.includes(pathString);
    }
  };

  return navigate;
};

export default useNavigation;
